import { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { useWebSocket } from '../hooks/useWebSocket';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://127.0.0.1:3041';

const ALARM_TYPES = [
  { key: 'HIGH_TEMP',      label: 'Temp. e lartë' },
  { key: 'LOW_TEMP',       label: 'Temp. e ulët'  },
  { key: 'HIGH_HUMIDITY',  label: 'Lag. e lartë'  },
  { key: 'LOW_HUMIDITY',   label: 'Lag. e ulët'   },
  { key: 'SENSOR_OFFLINE', label: 'Sensor offline' },
];
const SEVERITIES = ['WARNING','ALARM','CRITICAL'];

const inputCls = "w-full px-2 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500";
const selectCls = "w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500";
const cardCls = "bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700";

// 4 rreshta × 3 kolona
const THRESHOLD_ROWS = [
  {
    label: 'Temperatura e lartë',
    unit: '°C',
    color: 'text-orange-600 dark:text-orange-400',
    bg: 'bg-orange-50 dark:bg-orange-900/10',
    hint: 'Kur temperatura kalon kufirin:',
    fields: {
      warning:  'temp_high_warning',
      alarm:    'temp_high_alarm',
      critical: 'temp_high_critical',
    },
    placeholder: ['25', '28', '32'],
  },
  {
    label: 'Temperatura e ulët',
    unit: '°C',
    color: 'text-blue-600 dark:text-blue-400',
    bg: 'bg-blue-50 dark:bg-blue-900/10',
    hint: 'Kur temperatura bie nën kufirin:',
    fields: {
      warning:  'temp_low_warning',
      alarm:    'temp_low_alarm',
      critical: 'temp_low_critical',
    },
    placeholder: ['5', '2', '0'],
  },
  {
    label: 'Lagështia e lartë',
    unit: '%',
    color: 'text-cyan-600 dark:text-cyan-400',
    bg: 'bg-cyan-50 dark:bg-cyan-900/10',
    hint: 'Kur lagështia kalon kufirin:',
    fields: {
      warning:  'humidity_high_warning',
      alarm:    'humidity_high_alarm',
      critical: 'humidity_high_critical',
    },
    placeholder: ['80', '88', '95'],
  },
  {
    label: 'Lagështia e ulët',
    unit: '%',
    color: 'text-indigo-600 dark:text-indigo-400',
    bg: 'bg-indigo-50 dark:bg-indigo-900/10',
    hint: 'Kur lagështia bie nën kufirin:',
    fields: {
      warning:  'humidity_low_warning',
      alarm:    'humidity_low_alarm',
      critical: 'humidity_low_critical',
    },
    placeholder: ['30', '20', '10'],
  },
];

const SEV_STYLES = {
  warning:  { label: '⚠️ Warning',  badge: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300', border: 'border-amber-200 dark:border-amber-700' },
  alarm:    { label: '🔔 Alarm',    badge: 'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300', border: 'border-orange-200 dark:border-orange-700' },
  critical: { label: '🚨 Critical', badge: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300', border: 'border-red-200 dark:border-red-700' },
};

const EMPTY_THRESH = {
  temp_high_warning:'', temp_high_alarm:'', temp_high_critical:'',
  temp_low_warning:'',  temp_low_alarm:'',  temp_low_critical:'',
  humidity_high_warning:'', humidity_high_alarm:'', humidity_high_critical:'',
  humidity_low_warning:'',  humidity_low_alarm:'',  humidity_low_critical:'',
};

export default function Control() {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Të gjitha hooks PARA çdo return të kushtëzuar
  const [companies, setCompanies] = useState([]);
  const [dataLoggers, setDataLoggers] = useState([]);
  const [sensors, setSensors] = useState([]);

  const [selCompany, setSelCompany] = useState('');
  const [selLogger, setSelLogger]  = useState('');
  const [selSensor, setSelSensor]  = useState('');

  const [sensorData, setSensorData] = useState(null);
  const [thresholds, setThresholds] = useState(EMPTY_THRESH);

  const [alarmRule, setAlarmRule] = useState(null);
  const [alarmForm, setAlarmForm] = useState({ alarm_types:[], severities:[], email_recipients:[] });
  const [emailInput, setEmailInput] = useState('');

  const [savingT, setSavingT] = useState(false);
  const [savingR, setSavingR] = useState(false);
  const [perimeter, setPerimeter] = useState('');
  const [savingP, setSavingP] = useState(false);

  const auth = { Authorization: `Bearer ${localStorage.getItem('access_token')}` };
  const canSetPerimeter = ['SUPERADMIN','AVUK','KRYEINSPEKTORI'].includes(user?.role);

  // URL param ?sensor=
  useEffect(() => {
    const sid = new URLSearchParams(location.search).get('sensor');
    if (sid) setSelSensor(sid);
  }, [location.search]);

  // Companies
  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/companies`, { headers: auth })
      .then(r => setCompanies(r.data || [])).catch(() => {});
  }, []);

  // Data loggers kur ndryshon kompania
  useEffect(() => {
    if (!selCompany) { setDataLoggers([]); setSelLogger(''); setSensors([]); setSelSensor(''); return; }
    axios.get(`${BACKEND_URL}/api/data-loggers?company_id=${selCompany}`, { headers: auth })
      .then(r => setDataLoggers(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    // Perimetri i kompanisë
    const comp = companies.find(c => String(c.id) === String(selCompany));
    setPerimeter(comp?.perimeter_m ?? 100);
    // Alarm rule per kompani
    axios.get(`${BACKEND_URL}/api/alarm-rules?company_id=${selCompany}`, { headers: auth })
      .then(r => {
        const rule = Array.isArray(r.data) ? r.data[0] : r.data;
        if (rule) {
          setAlarmRule(rule);
          setAlarmForm({ alarm_types: rule.alarm_types||[], severities: rule.severities||[], email_recipients: rule.email_recipients||[] });
        } else {
          setAlarmRule(null);
          setAlarmForm({ alarm_types:[], severities:[], email_recipients:[] });
        }
      }).catch(() => { setAlarmRule(null); setAlarmForm({ alarm_types:[], severities:[], email_recipients:[] }); });
  }, [selCompany]);

  // Sensors kur ndryshon logger
  useEffect(() => {
    if (!selLogger) { setSensors([]); setSelSensor(''); return; }
    axios.get(`${BACKEND_URL}/api/sensors?data_logger_id=${selLogger}`, { headers: auth })
      .then(r => setSensors(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, [selLogger]);

  // WebSocket — update-on live temp/lagështia e sensorit të zgjedhur
  useWebSocket(useCallback((reading) => {
    if (!selSensor || reading.sensor_id !== selSensor) return;
    setSensorData(prev => prev
      ? { ...prev, temperature: reading.temperature, humidity: reading.humidity, status: 'online' }
      : prev
    );
  }, [selSensor]));

  // Sensor data kur zgjedhet sensori
  useEffect(() => {
    if (!selSensor) { setSensorData(null); setThresholds(EMPTY_THRESH); return; }
    axios.get(`${BACKEND_URL}/api/sensors/${selSensor}`, { headers: auth })
      .then(r => {
        const s = r.data;
        setSensorData(s);
        setThresholds({
          temp_high_warning:    s.temp_high_warning    ?? '',
          temp_high_alarm:      s.temp_high_alarm      ?? '',
          temp_high_critical:   s.temp_high_critical   ?? '',
          temp_low_warning:     s.temp_low_warning     ?? '',
          temp_low_alarm:       s.temp_low_alarm       ?? '',
          temp_low_critical:    s.temp_low_critical    ?? '',
          humidity_high_warning:  s.humidity_high_warning  ?? '',
          humidity_high_alarm:    s.humidity_high_alarm    ?? '',
          humidity_high_critical: s.humidity_high_critical ?? '',
          humidity_low_warning:   s.humidity_low_warning   ?? '',
          humidity_low_alarm:     s.humidity_low_alarm     ?? '',
          humidity_low_critical:  s.humidity_low_critical  ?? '',
        });
        if (s.company_id && !selCompany) setSelCompany(String(s.company_id));
        if (s.data_logger_id && !selLogger) setSelLogger(String(s.data_logger_id));
      }).catch(() => {});
  }, [selSensor]);

  const saveThresholds = async () => {
    if (!selSensor) return;
    setSavingT(true);
    try {
      const payload = {};
      Object.entries(thresholds).forEach(([k,v]) => {
        payload[k] = v !== '' ? parseFloat(v) : null;
      });
      await axios.put(`${BACKEND_URL}/api/sensors/${selSensor}`, payload, { headers: auth });
      toast.success('Pragjet u ruajtën');
    } catch { toast.error('Gabim gjatë ruajtjes'); }
    finally { setSavingT(false); }
  };

  const saveAlarmRule = async () => {
    if (!selCompany) return;
    setSavingR(true);
    try {
      const payload = { ...alarmForm, company_id: selCompany };
      if (alarmRule?.id) {
        await axios.put(`${BACKEND_URL}/api/alarm-rules/${alarmRule.id}`, payload, { headers: auth });
      } else {
        const res = await axios.post(`${BACKEND_URL}/api/alarm-rules`, payload, { headers: auth });
        setAlarmRule(res.data);
      }
      toast.success('Rregullat u ruajtën');
    } catch { toast.error('Gabim gjatë ruajtjes'); }
    finally { setSavingR(false); }
  };

  const savePerimeter = async () => {
    if (!selCompany) return;
    setSavingP(true);
    try {
      await axios.put(`${BACKEND_URL}/api/companies/${selCompany}`, { perimeter_m: parseFloat(perimeter) || 100 }, { headers: auth });
      // Refresh companies list
      const r = await axios.get(`${BACKEND_URL}/api/companies`, { headers: auth });
      setCompanies(r.data || []);
      toast.success(`Perimetri u vendos: ${perimeter}m`);
    } catch { toast.error('Gabim gjatë ruajtjes'); }
    finally { setSavingP(false); }
  };

  const toggleArr = (arr, item) => arr.includes(item) ? arr.filter(x=>x!==item) : [...arr, item];

  const addEmail = () => {
    const e = emailInput.trim();
    if (!e || alarmForm.email_recipients.includes(e)) return;
    if (!/\S+@\S+\.\S+/.test(e)) { toast.error('Email i pavlefshëm'); return; }
    setAlarmForm(f => ({ ...f, email_recipients: [...f.email_recipients, e] }));
    setEmailInput('');
  };

  const companyName = companies.find(c => String(c.id) === String(selCompany))?.name || '';

  // Early return PAS hooks — rule i React
  if (user?.role === 'KOMPANIA') {
    return (
      <Layout>
        <div className="p-8 flex flex-col items-center justify-center min-h-96 gap-4">
          <div className="w-16 h-16 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center text-3xl">🔒</div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Akses i kufizuar</h2>
          <p className="text-gray-500 dark:text-gray-400 text-sm text-center max-w-sm">
            Ky seksion është i disponueshëm vetëm për administratorët, avukatët dhe inspektorët. Kompania nuk ka të drejta të ndryshojë parametrat e alarmit.
          </p>
          <button onClick={() => navigate('/')} className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition">
            Kthehu në Dashboard
          </button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-4 space-y-4">
        <h1 className="text-xl font-bold text-gray-900 dark:text-white">Kontrolli i Parametrave</h1>

        {/* Zgjidhni sensor */}
        <div className={`${cardCls} p-4`}>
          <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Zgjidhni Sensorin</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Kompania</label>
              <select value={selCompany} onChange={e=>{setSelCompany(e.target.value);setSelLogger('');setSelSensor('');}} className={selectCls}>
                <option value="">-- Zgjidhni --</option>
                {companies.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Data Logger</label>
              <select value={selLogger} onChange={e=>{setSelLogger(e.target.value);setSelSensor('');}} className={selectCls} disabled={!selCompany}>
                <option value="">-- Zgjidhni --</option>
                {dataLoggers.map(d=><option key={d.id} value={d.id}>{d.name||d.serial_number}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Sensori</label>
              <select value={selSensor} onChange={e=>setSelSensor(e.target.value)} className={selectCls} disabled={!selLogger}>
                <option value="">-- Zgjidhni --</option>
                {sensors.map(s=><option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
          </div>
        </div>

        {/* Pragjet e alarmit */}
        {sensorData && (
          <div className={`${cardCls} p-4`}>
            <div className="flex items-start justify-between mb-4 gap-3">
              <div>
                <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                  Pragjet e Alarmit — <span className="text-blue-600 dark:text-blue-400">{sensorData.name}</span>
                </h2>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  {sensorData.temperature != null && <>Temp: <span className="text-orange-600 dark:text-orange-400 font-medium">{sensorData.temperature}°C</span></>}
                  {sensorData.humidity != null && <> · Lag: <span className="text-blue-600 dark:text-blue-400 font-medium">{sensorData.humidity}%</span></>}
                </p>
              </div>
              <button onClick={saveThresholds} disabled={savingT}
                className="flex-shrink-0 px-3 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-xs font-semibold rounded-lg transition">
                {savingT ? '...' : '💾 Ruaj'}
              </button>
            </div>

            {/* ── MOBILE: karta per parametër ── */}
            <div className="md:hidden space-y-1.5">
              {/* Header severity */}
              <div className="grid grid-cols-3 gap-1.5 mb-1 px-0.5">
                {['warning','alarm','critical'].map(k => (
                  <div key={k} className="text-center">
                    <span className={`inline-block px-1.5 py-0.5 rounded text-xs font-semibold ${SEV_STYLES[k].badge}`}>
                      {k === 'warning' ? '⚠️ Warn' : k === 'alarm' ? '🔔 Alarm' : '🚨 Crit'}
                    </span>
                  </div>
                ))}
              </div>
              {THRESHOLD_ROWS.map((row, ri) => (
                <div key={ri} className={`rounded-lg px-2.5 py-2 ${row.bg}`}>
                  <div className="flex items-center gap-1.5 mb-1">
                    <span className={`text-xs font-semibold ${row.color}`}>{row.label}</span>
                    <span className="text-xs text-gray-400 ml-auto">{row.unit}</span>
                  </div>
                  <div className="grid grid-cols-3 gap-1.5">
                    {['warning','alarm','critical'].map((sev, si) => {
                      const fieldKey = row.fields[sev];
                      return (
                        <div key={sev} className={`rounded border bg-white dark:bg-gray-800 ${SEV_STYLES[sev].border}`}>
                          <input
                            type="number"
                            step="0.1"
                            value={thresholds[fieldKey]}
                            onChange={e => setThresholds(p => ({ ...p, [fieldKey]: e.target.value }))}
                            placeholder={row.placeholder[si]}
                            className="w-full bg-transparent text-gray-900 dark:text-white text-xs focus:outline-none text-center font-semibold py-1 px-0.5"
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            {/* ── DESKTOP: tabela 4×3 ── */}
            <div className="hidden md:block">
              <div className="grid grid-cols-4 gap-2 mb-2 px-1">
                <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Parametri</div>
                {['warning','alarm','critical'].map(k => (
                  <div key={k} className="text-center">
                    <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${SEV_STYLES[k].badge}`}>
                      {SEV_STYLES[k].label}
                    </span>
                  </div>
                ))}
              </div>
              <div className="space-y-2">
                {THRESHOLD_ROWS.map((row, ri) => (
                  <div key={ri} className={`grid grid-cols-4 gap-2 rounded-lg p-3 ${row.bg}`}>
                    <div className="flex flex-col justify-center">
                      <span className={`text-sm font-medium ${row.color}`}>{row.label}</span>
                      <span className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{row.hint}</span>
                    </div>
                    {['warning','alarm','critical'].map((sev, si) => {
                      const fieldKey = row.fields[sev];
                      return (
                        <div key={sev} className={`rounded-lg border p-2 bg-white dark:bg-gray-800 ${SEV_STYLES[sev].border}`}>
                          <div className="flex items-center gap-1">
                            <input
                              type="number"
                              step="0.1"
                              value={thresholds[fieldKey]}
                              onChange={e => setThresholds(p => ({ ...p, [fieldKey]: e.target.value }))}
                              placeholder={row.placeholder[si]}
                              className="w-full bg-transparent text-gray-900 dark:text-white text-sm focus:outline-none text-center font-medium"
                            />
                            <span className="text-xs text-gray-400 flex-shrink-0">{row.unit}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>

            {/* Legjenda */}
            <div className="mt-3 p-3 rounded-lg bg-gray-50 dark:bg-gray-700/30 text-xs text-gray-500 dark:text-gray-400">
              <span className="font-medium text-gray-700 dark:text-gray-300">Si funksionon:</span>
              {' '}Temperatura e lartë: nëse kalon <span className={`${SEV_STYLES.warning.badge} px-1 rounded`}>Warning</span> → njoftim i hershëm.
              {' '}Nëse kalon <span className={`${SEV_STYLES.alarm.badge} px-1 rounded`}>Alarm</span> → alarm mesatar.
              {' '}Nëse kalon <span className={`${SEV_STYLES.critical.badge} px-1 rounded`}>Critical</span> → alarm urgjent.
            </div>
          </div>
        )}

        {/* Perimetri i zgjidhjes — vetëm KRYEINSPEKTORI/SUPERADMIN/AVUK */}
        {selCompany && canSetPerimeter && (
          <div className={`${cardCls} p-4`}>
            <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-2 mb-1">
              📍 Perimetri i Zgjidhjes
              <span className="text-xs font-normal text-gray-400">— {companyName}</span>
            </h2>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
              Rrezja maksimale nga sensori ku inspektori mund të zgjidhë alarmin (Haversine). Nëse inspektori është jashtë perimetrit, do të shënohet me ⚠️.
            </p>
            {/* Inputi + butonat quick + Ruaj — të gjitha flex-wrap */}
            <div className="flex flex-wrap items-center gap-2">
              <div className="relative">
                <input
                  type="number"
                  min="10" max="10000" step="10"
                  value={perimeter}
                  onChange={e => setPerimeter(e.target.value)}
                  className="w-24 px-3 py-2 pr-7 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-center font-medium"
                />
                <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-gray-400 pointer-events-none">m</span>
              </div>
              {[50,100,200,500].map(v => (
                <button key={v} onClick={() => setPerimeter(v)}
                  className={`px-3 py-2 rounded-lg text-xs font-semibold border transition ${
                    Number(perimeter)===v
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-400 border-gray-300 dark:border-gray-600 hover:border-blue-400'
                  }`}>
                  {v}m
                </button>
              ))}
              <button onClick={savePerimeter} disabled={savingP}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-xs font-semibold rounded-lg transition">
                {savingP ? '...' : '💾 Ruaj'}
              </button>
            </div>
          </div>
        )}

        {/* Rregullat e njoftimit */}
        {selCompany && (
          <div className={`${cardCls} p-4`}>
            <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4">
              Rregullat e Njoftimit — <span className="text-blue-600 dark:text-blue-400">{companyName}</span>
            </h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Tipi alarmi */}
              <div>
                <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-3">Tipi i alarmit</h3>
                <div className="space-y-2">
                  {ALARM_TYPES.map(({key,label}) => (
                    <label key={key} className="flex items-center gap-2 cursor-pointer group">
                      <input type="checkbox"
                        checked={alarmForm.alarm_types.includes(key)}
                        onChange={() => setAlarmForm(f=>({...f, alarm_types: toggleArr(f.alarm_types, key)}))}
                        className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500" />
                      <span className="text-sm text-gray-700 dark:text-gray-300 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition">{label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Serioziteti */}
              <div>
                <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-3">Reago për seriozitetin</h3>
                <div className="space-y-2">
                  {SEVERITIES.map(sev => (
                    <label key={sev} className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox"
                        checked={alarmForm.severities.includes(sev)}
                        onChange={() => setAlarmForm(f=>({...f, severities: toggleArr(f.severities, sev)}))}
                        className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500" />
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded ${SEV_STYLES[sev.toLowerCase()]?.badge || 'bg-gray-100 text-gray-700'}`}>{sev}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Email-et */}
              <div>
                <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-3">Email-et e njoftimit</h3>
                <div className="flex gap-2 mb-2">
                  <input type="email" value={emailInput} onChange={e=>setEmailInput(e.target.value)}
                    onKeyDown={e=>e.key==='Enter'&&addEmail()}
                    placeholder="email@shembull.com" className={selectCls} />
                  <button onClick={addEmail}
                    className="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition whitespace-nowrap">
                    + Shto
                  </button>
                </div>
                <div className="space-y-1 max-h-32 overflow-y-auto">
                  {alarmForm.email_recipients.length === 0
                    ? <p className="text-xs text-gray-400 dark:text-gray-500">Asnjë email i shtuar</p>
                    : alarmForm.email_recipients.map(em => (
                      <div key={em} className="flex items-center justify-between gap-2 bg-gray-50 dark:bg-gray-700 rounded-lg px-3 py-1.5">
                        <span className="text-xs text-gray-700 dark:text-gray-300 truncate">{em}</span>
                        <button onClick={()=>setAlarmForm(f=>({...f,email_recipients:f.email_recipients.filter(x=>x!==em)}))}
                          className="text-gray-400 hover:text-red-500 transition text-lg leading-none">&times;</button>
                      </div>
                    ))
                  }
                </div>
              </div>
            </div>

            <div className="mt-4 flex justify-end">
              <button onClick={saveAlarmRule} disabled={savingR}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition">
                {savingR ? 'Duke ruajtur...' : '💾 Ruaj Rregullat'}
              </button>
            </div>
          </div>
        )}

        {!selSensor && !selCompany && (
          <div className={`${cardCls} p-10 text-center`}>
            <div className="text-4xl mb-3">⚙️</div>
            <p className="text-gray-500 dark:text-gray-400 text-sm">
              Zgjidhni kompaninë, data loggerin dhe sensorin për të konfiguruar pragjet e alarmit.
            </p>
          </div>
        )}
      </div>
    </Layout>
  );
}
